/**
 * Native `<video>` player for self-hosted journey map video steps, with watched-coverage
 * completion tracking.
 *
 * Why a native element rather than an embedded iframe: media events (`timeupdate`, `pause`,
 * `ended`) fire on an element that lives in our own DOM regardless of where the bytes come from,
 * so we get a real per-learner signal for how much has actually been watched. An iframe from a
 * hosting provider gives no such signal. The bytes themselves are streamed straight from a signed
 * S3 GET URL handed to the element's `src` — exactly like the pdf/image renderers — which needs no
 * bucket CORS rule, because a media element load (like `<img>`/`<iframe>`) is not a CORS fetch.
 *
 * Coverage is measured as the count of distinct whole seconds that have played (a Set of
 * `floor(currentTime)`), divided by the video's duration. That is naturally resistant to seeking:
 * scrubbing backward re-marks seconds already counted, and skipping forward never marks the
 * skipped seconds, so the percentage reflects what was actually watched rather than how far the
 * playhead reached. The number is reported to the server, which enforces the completion threshold
 * and only ever moves its stored coverage up — see `support/journeyMap/videoProgress.ts`.
 *
 * Signed read URLs expire (default one hour, i.e. right at the video-length ceiling). If playback
 * outlives the URL — a paused-then-resumed session, or a seek after the buffer is exhausted — the
 * element raises an `error`; we refetch a fresh URL once and resume from the last position rather
 * than surfacing a failure. (CloudFront signed cookies would remove this concern entirely and can
 * be added later without touching this component; see the plan's deferred section.)
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';
import { getResourceFileDownload } from '../../../support/resourceFiles';
import {
  computeCoveragePct,
  DEFAULT_VIDEO_COMPLETION_THRESHOLD_PCT,
  recordJourneyMapVideoProgress,
} from '../../../support/journeyMap/videoProgress';

type Props = {
  /** `resource.id` of the file-backed video resource (from `provider_config.file`). */
  resourceId: number | string;
  /** The journey map item; passed straight back to `onVideoCompleted`. */
  item: any;
  /** Percent of the video that must be watched to credit completion. */
  thresholdPct?: number;
  /** Active assignment id, if the page already has it. The API resolves it otherwise. */
  assignmentId?: number | string | null;
  /** Bumped when the underlying file is replaced; re-fetches the URL and resets coverage. */
  version?: number | null;
  /**
   * Seconds to resume playback from when the step is reopened — the server's stored
   * `evidence.video.last_position_sec`. Applied once, on the first loaded metadata, and only when
   * it lands inside the video. Coverage is unaffected: seeking here does not mark seconds watched.
   */
  resumePositionSec?: number | null;
  /** Fired once, on the heartbeat that crosses the threshold, so the page can refresh progress. */
  onVideoCompleted?: (item: any) => void;
};

/** Report at most this often during playback; pause/ended always flush immediately. */
const HEARTBEAT_MS = 15000;

const JourneyMapVideoPlayer: React.FC<Props> = ({
  resourceId,
  item,
  thresholdPct,
  assignmentId,
  version,
  resumePositionSec,
  onVideoCompleted,
}) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  // Flipped once the element has enough metadata (duration) to seek. Kept as state — not a ref —
  // so the resume effect below re-evaluates when the metadata becomes ready.
  const [metadataReady, setMetadataReady] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const watchedSecondsRef = useRef<Set<number>>(new Set());
  const durationRef = useRef(0);
  const lastSentAtRef = useRef(0);
  const completedRef = useRef(false);
  // Guards the one-shot URL refresh so an unrecoverable object cannot loop forever.
  const urlRefreshedRef = useRef(false);
  const inFlightRef = useRef(false);
  // Guards the one-shot resume seek so it happens on the first load only, not on every metadata
  // event or after an in-session URL refresh (which restores its own position).
  const resumeAppliedRef = useRef(false);

  const fetchUrl = useCallback(async () => {
    // `inline` so the object is served for playback rather than as an attachment download.
    const download = await getResourceFileDownload(resourceId, { inline: true });
    return download.url;
  }, [resourceId]);

  // Load (and reset) whenever the resource or its version changes.
  useEffect(() => {
    let cancelled = false;

    watchedSecondsRef.current = new Set();
    durationRef.current = 0;
    lastSentAtRef.current = 0;
    completedRef.current = false;
    urlRefreshedRef.current = false;
    setMetadataReady(false);
    setCompleted(false);
    setError('');
    setLoading(true);
    setUrl('');

    fetchUrl()
      .then((fresh) => {
        if (!cancelled) setUrl(fresh);
      })
      .catch((err: any) => {
        if (!cancelled) setError(String(err?.message || 'Unable to load this video.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchUrl, version]);

  const coveragePct = useCallback(
    () => computeCoveragePct(watchedSecondsRef.current, durationRef.current),
    [],
  );

  const sendProgress = useCallback(
    async (force: boolean) => {
      const duration = durationRef.current;
      if (!duration || inFlightRef.current) return;

      const now = Date.now();
      if (!force && now - lastSentAtRef.current < HEARTBEAT_MS) return;

      lastSentAtRef.current = now;
      inFlightRef.current = true;

      try {
        const result = await recordJourneyMapVideoProgress(item?.id, {
          coveragePct: coveragePct(),
          positionSec: videoRef.current?.currentTime ?? null,
          durationSec: duration,
          assignmentId,
        });

        if ((result.completed || result.newlyCompleted) && !completedRef.current) {
          completedRef.current = true;
          setCompleted(true);
          if (result.newlyCompleted) onVideoCompleted?.(item);
        }
      } catch {
        // A dropped heartbeat is not worth interrupting playback over; the next one carries the
        // (monotonic) coverage anyway.
      } finally {
        inFlightRef.current = false;
      }
    },
    [assignmentId, coveragePct, item, onVideoCompleted],
  );

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (Number.isFinite(video.duration)) durationRef.current = video.duration;
    setMetadataReady(true);
  }, []);

  // Resume where the learner left off, once. Attempted from an effect (metadata + position ready)
  // AND again on `canplay`, and considered "applied" only once the playhead has actually moved
  // near the target — so neither of the two real failure modes wins:
  //   1. Ordering: the element can load its metadata BEFORE the progress row (and thus
  //      `resumePositionSec`) arrives; a one-shot fired then would burn itself with no position.
  //   2. Seekability: a seek assigned at `loadedmetadata` sometimes does not stick until the media
  //      is buffered enough, leaving `currentTime` at 0.
  // Seeking here does not mark seconds watched, so coverage is unaffected.
  const applyResume = useCallback(() => {
    if (resumeAppliedRef.current) return;
    const video = videoRef.current;
    if (!video) return;

    const resumeAt = Number(resumePositionSec);
    if (!Number.isFinite(resumeAt) || resumeAt <= 0) return;

    const duration = durationRef.current || video.duration;
    if (Number.isFinite(duration) && duration > 0 && resumeAt >= duration - 1) {
      // Effectively fully watched — nothing to resume into; do not seek to the very end.
      resumeAppliedRef.current = true;
      return;
    }

    try {
      video.currentTime = resumeAt;
    } catch {
      /* seeking before the element is ready can throw; a later `canplay` retry handles it */
      return;
    }
    // Only latch success once the seek actually took; otherwise leave it for the next attempt.
    if (Math.abs(video.currentTime - resumeAt) < 1.5) resumeAppliedRef.current = true;
  }, [resumePositionSec]);

  useEffect(() => {
    if (metadataReady) applyResume();
  }, [metadataReady, applyResume]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    watchedSecondsRef.current.add(Math.floor(video.currentTime));
    sendProgress(false);
  }, [sendProgress]);

  const handlePause = useCallback(() => sendProgress(true), [sendProgress]);
  const handleEnded = useCallback(() => sendProgress(true), [sendProgress]);

  // Flush a final heartbeat when the modal closes mid-play so partial coverage is not lost.
  useEffect(() => () => { sendProgress(true); }, [sendProgress]);

  const handleError = useCallback(async () => {
    if (urlRefreshedRef.current) {
      setError('This video could not be played. Try reopening the step.');
      return;
    }

    urlRefreshedRef.current = true;

    try {
      const fresh = await fetchUrl();
      const video = videoRef.current;
      const resumeAt = video?.currentTime ?? 0;

      setUrl(fresh);

      // Restore the playhead once the refreshed source has loaded enough to seek.
      if (video) {
        const restore = () => {
          try {
            if (resumeAt > 0) video.currentTime = resumeAt;
            void video.play();
          } catch {
            /* autoplay/seek may be refused; the learner can press play */
          }
          video.removeEventListener('loadedmetadata', restore);
        };
        video.addEventListener('loadedmetadata', restore);
      }
    } catch {
      setError('This video could not be played. Try reopening the step.');
    }
  }, [fetchUrl]);

  const threshold = thresholdPct ?? DEFAULT_VIDEO_COMPLETION_THRESHOLD_PCT;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
        <HbrSpinner />
      </div>
    );
  }

  if (error) {
    return <div style={{ fontSize: '13px', color: '#CC2D37' }}>{error}</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <video
        ref={videoRef}
        src={url}
        controls
        // `nodownload` removes Chromium's download button. It is not honored everywhere (Firefox
        // ignores controlsList), so the right-click menu — the other route to "Save video as" — is
        // suppressed too, and Picture-in-Picture (which exposes its own download/save affordance) is
        // disabled. None of this makes the signed URL unreachable to a determined user, but it keeps
        // the preview from *offering* a download, which is the intent for a tracked learning step.
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={applyResume}
        onTimeUpdate={handleTimeUpdate}
        onPause={handlePause}
        onEnded={handleEnded}
        onError={handleError}
        style={{
          width: '100%',
          maxHeight: '70vh',
          background: '#000',
          borderRadius: '4px',
          border: '1px solid #E1E4E8',
        }}
      />
      <div style={{ fontSize: '12px', color: '#6F7680' }}>
        {completed
          ? 'Completed — you have watched enough of this video.'
          : `Watch at least ${Math.round(threshold)}% of this video to complete this step.`}
      </div>
    </div>
  );
};

export default JourneyMapVideoPlayer;
